import React, { useMemo } from "react";
import classNames from "classnames";
import hljs from "highlight.js/lib/core";
import sqlLangDef from "highlight.js/lib/languages/sql";

type Props = {
  sql: string;
  inline?: boolean;
  keepWhitespace?: boolean;
  nowrap?: boolean;
  className?: string;
};

// Register language once at module load
hljs.registerLanguage("sql", sqlLangDef);

// Add alias rule for $param_name highlighting
const ALIAS = {
  className: "alias",
  begin: /\$[a-z][a-z0-9_]*/,
};
const lang = hljs.getLanguage("sql");
lang?.contains.push(ALIAS);

// Keywords that should not be highlighted
const unhighlightedKeywords = new Set([
  "oid",
  "date",
  "year",
  "month",
  "unique",
  "desc",
  "asc",
  "interval",
  "null",
  "not",
  "upsert",
  "name",
  "position",
  "is",
  "id",
  "cleanup",
  "query",
  "extract",
  "coalesce",
]);

function processHighlightedHTML(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  for (const span of doc.querySelectorAll("span")) {
    const cls = span.className;
    if (cls === "hljs-keyword") {
      if (unhighlightedKeywords.has(span.textContent!.toLowerCase())) {
        span.replaceWith(span.textContent!);
      } else {
        span.className = "uppercase break-normal text-sql-keyword";
      }
    } else if (cls === "hljs-string" || cls === "hljs-number") {
      span.className = "text-sql-default";
    } else if (cls === "hljs-aggregate") {
      span.className = "text-inherit";
    } else if (cls === "hljs-comment") {
      span.className = "text-sql-comment";
    } else if (cls === "hljs-alias") {
      span.className = "hljs-alias text-sql-alias";
    } else if (cls === "hljs-literal") {
      span.className = "";
    }
  }
  return doc.body.innerHTML;
}

const SQL = ({ sql, inline, keepWhitespace, nowrap, className }: Props) => {
  const highlightedHTML = useMemo(() => {
    let normalized = sql;

    if (inline) {
      normalized = normalized.replace(/\s+/g, " ");
    } else if (!keepWhitespace) {
      normalized = normalized
        .replace(/[^\S\n]+/g, " ")
        .replace(/\n\s/g, "\n")
        .replace(/\n{1,}/g, "\n");
    }

    const result = hljs.highlight(normalized, { language: "sql" });
    return processHighlightedHTML(result.value);
  }, [sql, inline, keepWhitespace]);

  return (
    <pre
      className={classNames(
        "border-none m-0 p-0 bg-none bg-transparent",
        !nowrap && "whitespace-pre-wrap",
        inline && "inline",
        className,
      )}
      dangerouslySetInnerHTML={{ __html: highlightedHTML }}
    />
  );
};

export default SQL;
